import { Injectable } from '@angular/core';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import { MasterPartialState } from './master.reducer';
import {
  LoadMaster,
  MasterLoaded,
  MasterLoadError,
  MasterActionTypes,
  ShipperCreated,
  CreateShipper,
  CreateShipperError,
  LoadShipper,
  ShipperLoaded,
  ShipperLoadError,
  UpdateShipper,
  UpdateShipperError,
  ShipperUpdated,
  CreateConsignee,
  ConsigneeCreated,
  CreateConsigneeError,
  LoadConsignee,
  ConsigneeLoaded,
  ConsigneeLoadError,
  UpdateConsignee,
  ConsigneeUpdated,
  UpdateConsigneeError,
  CreateNotify,
  NotifyCreated,
  CreateNotifyError,
  LoadNotify,
  NotifyLoaded,
  NotifyLoadError,
  UpdateNotify,
  NotifyUpdated,
  UpdateNotifyError,
  CreateDeliveryAgent,
  DeliveryAgentCreated,
  CreateDeliveryAgentError,
  LoadDeliveryAgent,
  DeliveryAgentLoaded,
  DeliveryAgentLoadError,
  UpdateDeliveryAgent,
  DeliveryAgentUpdated,
  UpdateDeliveryAgentError,
  DeleteEntity,
  EntityDeleted,
  DeleteEntityError,
  CreateAirportCode,
  AirportCodeCreated,
  CreateAirportCodeError,
  LoadAirportCode,
  AirportCodeLoaded,
  AirportCodeLoadError,
  CreateCarrierCode,
  CarrierCodeCreated,
  CreateCarrierCodeError,
  LoadCarrierCode,
  CarrierCodeLoadError,
  CarrierCodeLoaded,
  CreateDestinationAirport,
  CreateDestinationAirportError,
  DestinationAirportCreated,
  LoadDestinationAirport,
  DestinationAirportLoaded,
  DestinationAirportLoadError,
  CreateAirline,
  AirlineCreated,
  CreateAirlineError,
  LoadAirline,
  AirlineLoadError,
  AirlineLoaded,
} from './master.actions';
import { from } from 'rxjs';
import { API } from 'aws-amplify';
import * as queries from '../../../../../../apps/website/src/queries';
import * as mutations from '../../../../../../apps/website/src/mutations';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api';
import { map } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class MasterEffects {
  @Effect() loadMaster$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadMaster),
    fetch({
      run: (action: LoadMaster, state: MasterPartialState) => {
        // Your custom REST 'load' logic goes here. For now just return an empty list...
        return new MasterLoaded([]);
      },

      onError: (action: LoadMaster, error) => {
        console.error('Error', error);
        return new MasterLoadError(error);
      },
    })
  );

  @Effect() createShipper$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateShipper),
    fetch({
      run: (action: CreateShipper, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createShipper,
            variables: {
              input: {
                name: action.payload.shipperName,
                address01: action.payload.shipperAddress01,
                address02: action.payload.shipperAddress02,
                address03: action.payload.shipperAddress03,
                address04: action.payload.shipperAddress04,
                type: 'shipper',
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Shipper Created Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new ShipperCreated(data.data.createShipper);
          })
        );
      },

      onError: (action: CreateShipper, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateShipperError(error);
      },
    })
  );

  @Effect() loadShipper$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadShipper),
    fetch({
      run: (action: LoadShipper, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.shipperByDate,
            variables: { limit: 10000, type: 'shipper' },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new ShipperLoaded(data.data.shipperByDate.items);
          })
        );
      },

      onError: (action: LoadShipper, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new ShipperLoadError(error);
      },
    })
  );

  @Effect() updateShipper$ = this.actions$.pipe(
    ofType(MasterActionTypes.UpdateShipper),
    fetch({
      run: (action: UpdateShipper, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.updateShipper,
            variables: {
              input: {
                id: action.id,
                name: action.payload.shipperName,
                address01: action.payload.shipperAddress01,
                address02: action.payload.shipperAddress02,
                address03: action.payload.shipperAddress03,
                address04: action.payload.shipperAddress04,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Shipper Updated Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new ShipperUpdated(data.data.updateShipper);
          })
        );
      },
      onError: (action: UpdateShipper, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new UpdateShipperError(error);
      },
    })
  );

  @Effect() shipperUpdated$ = this.actions$.pipe(
    ofType(MasterActionTypes.ShipperUpdated),
    fetch({
      run: (action: ShipperUpdated, state: MasterPartialState) => {
        return new LoadShipper();
      },
      onError: (action: ShipperUpdated, error) => {
        return new UpdateShipperError(error);
      },
    })
  );

  @Effect() createConsignee$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateConsignee),
    fetch({
      run: (action: CreateConsignee, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createConsignee,
            variables: {
              input: {
                name: action.payload.consigneeName,
                address01: action.payload.consigneeAddress01,
                address02: action.payload.consigneeAddress02,
                address03: action.payload.consigneeAddress03,
                address04: action.payload.consigneeAddress04,
                type: 'consignee',
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Consignee Created Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new ConsigneeCreated(data.data.createConsignee);
          })
        );
      },

      onError: (action: CreateConsignee, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateConsigneeError(error);
      },
    })
  );

  @Effect() loadConsignee$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadConsignee),
    fetch({
      run: (action: LoadConsignee, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.consigneeByDate,
            variables: { limit: 10000, type: 'consignee' },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new ConsigneeLoaded(data.data.consigneeByDate.items);
          })
        );
      },

      onError: (action: LoadConsignee, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new ConsigneeLoadError(error);
      },
    })
  );

  @Effect() updateConsignee$ = this.actions$.pipe(
    ofType(MasterActionTypes.UpdateConsignee),
    fetch({
      run: (action: UpdateConsignee, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.updateConsignee,
            variables: {
              input: {
                id: action.id,
                name: action.payload.consigneeName,
                address01: action.payload.consigneeAddress01,
                address02: action.payload.consigneeAddress02,
                address03: action.payload.consigneeAddress03,
                address04: action.payload.consigneeAddress04,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Consignee Updated Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new ConsigneeUpdated(data.data.updateConsignee);
          })
        );
      },
      onError: (action: UpdateConsignee, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new UpdateConsigneeError(error);
      },
    })
  );

  @Effect() consigneeUpdated$ = this.actions$.pipe(
    ofType(MasterActionTypes.ConsigneeUpdated),
    fetch({
      run: (action: ConsigneeUpdated, state: MasterPartialState) => {
        return new LoadConsignee();
      },
      onError: (action: ConsigneeUpdated, error) => {
        return new UpdateConsigneeError(error);
      },
    })
  );

  @Effect() createNotify$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateNotify),
    fetch({
      run: (action: CreateNotify, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createNotify,
            variables: {
              input: {
                name: action.payload.notifyName,
                address01: action.payload.notifyAddress01,
                address02: action.payload.notifyAddress02,
                address03: action.payload.notifyAddress03,
                address04: action.payload.notifyAddress04,
                type: 'notify',
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Notify Created Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new NotifyCreated(data.data.createNotify);
          })
        );
      },

      onError: (action: CreateNotify, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateNotifyError(error);
      },
    })
  );

  @Effect() loadNotify$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadNotify),
    fetch({
      run: (action: LoadNotify, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.notifyByDate,
            variables: { limit: 10000, type: 'notify' },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new NotifyLoaded(data.data.notifyByDate.items);
          })
        );
      },

      onError: (action: LoadNotify, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new NotifyLoadError(error);
      },
    })
  );

  @Effect() updateNotify$ = this.actions$.pipe(
    ofType(MasterActionTypes.UpdateNotify),
    fetch({
      run: (action: UpdateNotify, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.updateNotify,
            variables: {
              input: {
                id: action.id,
                name: action.payload.notifyName,
                address01: action.payload.notifyAddress01,
                address02: action.payload.notifyAddress02,
                address03: action.payload.notifyAddress03,
                address04: action.payload.notifyAddress04,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Notify Updated Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new NotifyUpdated(data.data.updateNotify);
          })
        );
      },
      onError: (action: UpdateNotify, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new UpdateNotifyError(error);
      },
    })
  );

  @Effect() notifyUpdated$ = this.actions$.pipe(
    ofType(MasterActionTypes.NotifyUpdated),
    fetch({
      run: (action: NotifyUpdated, state: MasterPartialState) => {
        return new LoadNotify();
      },
      onError: (action: NotifyUpdated, error) => {
        return new UpdateNotifyError(error);
      },
    })
  );

  @Effect() createDeliveryAgent$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateDeliveryAgent),
    fetch({
      run: (action: CreateDeliveryAgent, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createDeliveryAgent,
            variables: {
              input: {
                name: action.payload.deliveryAgentName,
                address01: action.payload.deliveryAgentAddress01,
                address02: action.payload.deliveryAgentAddress02,
                address03: action.payload.deliveryAgentAddress03,
                address04: action.payload.deliveryAgentAddress04,
                type: 'deliveryAgent',
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Delivery Agent Created Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new DeliveryAgentCreated(data.data.createDeliveryAgent);
          })
        );
      },

      onError: (action: CreateDeliveryAgent, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateDeliveryAgentError(error);
      },
    })
  );

  @Effect() loadDeliveryAgent$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadDeliveryAgent),
    fetch({
      run: (action: LoadDeliveryAgent, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.deliveryAgentByDate,
            variables: { limit: 10000, type: 'deliveryAgent' },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new DeliveryAgentLoaded(data.data.deliveryAgentByDate.items);
          })
        );
      },

      onError: (action: LoadDeliveryAgent, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new DeliveryAgentLoadError(error);
      },
    })
  );

  @Effect() updateDeliveryAgent$ = this.actions$.pipe(
    ofType(MasterActionTypes.UpdateDeliveryAgent),
    fetch({
      run: (action: UpdateDeliveryAgent, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.updateDeliveryAgent,
            variables: {
              input: {
                id: action.id,
                name: action.payload.deliveryAgentName,
                address01: action.payload.deliveryAgentAddress01,
                address02: action.payload.deliveryAgentAddress02,
                address03: action.payload.deliveryAgentAddress03,
                address04: action.payload.deliveryAgentAddress04,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Delivery Agent Updated Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new DeliveryAgentUpdated(data.data.updateDeliveryAgent);
          })
        );
      },
      onError: (action: UpdateDeliveryAgent, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new UpdateDeliveryAgentError(error);
      },
    })
  );

  @Effect() deliveryAgentUpdated$ = this.actions$.pipe(
    ofType(MasterActionTypes.DeliveryAgentUpdated),
    fetch({
      run: (action: DeliveryAgentUpdated, state: MasterPartialState) => {
        return new LoadDeliveryAgent();
      },
      onError: (action: DeliveryAgentUpdated, error) => {
        return new UpdateDeliveryAgentError(error);
      },
    })
  );

  @Effect() createAirportCode$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateAirportCode),
    fetch({
      run: (action: CreateAirportCode, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createAirportCode,
            variables: {
              input: {
                code: action.payload,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Airport Code Created Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new AirportCodeCreated(data.data.createAirportCode);
          })
        );
      },

      onError: (action: CreateAirportCode, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateAirportCodeError(error);
      },
    })
  );

  @Effect() loadAirportCode$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadAirportCode),
    fetch({
      run: (action: LoadAirportCode, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.listAirportCodes,
            variables: { limit: 10000 },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new AirportCodeLoaded(data.data.listAirportCodes.items);
          })
        );
      },

      onError: (action: LoadAirportCode, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new AirportCodeLoadError(error);
      },
    })
  );

  @Effect() createCarrierCode$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateCarrierCode),
    fetch({
      run: (action: CreateCarrierCode, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createCarrierCode,
            variables: {
              input: {
                code: action.payload,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Carrier Code Created Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new CarrierCodeCreated(data.data.createCarrierCode);
          })
        );
      },

      onError: (action: CreateCarrierCode, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateCarrierCodeError(error);
      },
    })
  );

  @Effect() loadCarrierCode$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadCarrierCode),
    fetch({
      run: (action: LoadCarrierCode, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.listCarrierCodes,
            variables: { limit: 10000 },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new CarrierCodeLoaded(data.data.listCarrierCodes.items);
          })
        );
      },

      onError: (action: LoadCarrierCode, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CarrierCodeLoadError(error);
      },
    })
  );

  @Effect() createDestinationAirport$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateDestinationAirport),
    fetch({
      run: (action: CreateDestinationAirport, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createDestinationAirport,
            variables: {
              input: {
                name: action.payload,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open(
              'Destination Airport Created Successfully',
              null,
              {
                duration: 5000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: 'custom-snackbar-success',
              }
            );
            return new DestinationAirportCreated(
              data.data.createDestinationAirport
            );
          })
        );
      },

      onError: (action: CreateDestinationAirport, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateDestinationAirportError(error);
      },
    })
  );

  @Effect() loadDestinationAirport$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadDestinationAirport),
    fetch({
      run: (action: LoadDestinationAirport, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.listDestinationAirports,
            variables: { limit: 10000 },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new DestinationAirportLoaded(
              data.data.listDestinationAirports.items
            );
          })
        );
      },

      onError: (action: LoadDestinationAirport, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new DestinationAirportLoadError(error);
      },
    })
  );

  @Effect() createAirline$ = this.actions$.pipe(
    ofType(MasterActionTypes.CreateAirline),
    fetch({
      run: (action: CreateAirline, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: mutations.createAirline,
            variables: {
              input: {
                name: action.payload,
                logoUrl: action.key,
              },
            },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            this._snackBar.open('Airline Created Successfully', null, {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: 'custom-snackbar-success',
            });
            return new AirlineCreated(data.data.createAirline);
          })
        );
      },

      onError: (action: CreateAirline, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new CreateAirlineError(error);
      },
    })
  );

  @Effect() loadAirline$ = this.actions$.pipe(
    ofType(MasterActionTypes.LoadAirline),
    fetch({
      run: (action: LoadAirline, state: MasterPartialState) => {
        return from(
          API.graphql({
            query: queries.listAirlines,
            variables: { limit: 10000 },
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
          })
        ).pipe(
          map((data: { data }) => {
            return new AirlineLoaded(data.data.listAirlines.items);
          })
        );
      },

      onError: (action: LoadAirline, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new AirlineLoadError(error);
      },
    })
  );

  @Effect() deleteEntity$ = this.actions$.pipe(
    ofType(MasterActionTypes.DeleteEntity),
    fetch({
      run: (action: DeleteEntity, state: MasterPartialState) => {
        switch (action.entityName) {
          case 'shipper': {
            return from(
              API.graphql({
                query: mutations.deleteShipper,
                variables: {
                  input: {
                    id: action.id,
                  },
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
              })
            ).pipe(
              map((data: { data }) => {
                this._snackBar.open('Shipper Deleted Successfully', null, {
                  duration: 5000,
                  horizontalPosition: 'right',
                  verticalPosition: 'top',
                  panelClass: 'custom-snackbar-success',
                });
                return new EntityDeleted(action.entityName);
              })
            );
          }
          case 'consignee': {
            return from(
              API.graphql({
                query: mutations.deleteConsignee,
                variables: {
                  input: {
                    id: action.id,
                  },
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
              })
            ).pipe(
              map((data: { data }) => {
                this._snackBar.open('Consignee Deleted Successfully', null, {
                  duration: 5000,
                  horizontalPosition: 'right',
                  verticalPosition: 'top',
                  panelClass: 'custom-snackbar-success',
                });
                return new EntityDeleted(action.entityName);
              })
            );
          }
          case 'notify': {
            return from(
              API.graphql({
                query: mutations.deleteNotify,
                variables: {
                  input: {
                    id: action.id,
                  },
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
              })
            ).pipe(
              map((data: { data }) => {
                this._snackBar.open('Notify Deleted Successfully', null, {
                  duration: 5000,
                  horizontalPosition: 'right',
                  verticalPosition: 'top',
                  panelClass: 'custom-snackbar-success',
                });
                return new EntityDeleted(action.entityName);
              })
            );
          }
          case 'deliveryAgent': {
            return from(
              API.graphql({
                query: mutations.deleteDeliveryAgent,
                variables: {
                  input: {
                    id: action.id,
                  },
                },
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
              })
            ).pipe(
              map((data: { data }) => {
                this._snackBar.open(
                  'Delivery Agent Deleted Successfully',
                  null,
                  {
                    duration: 5000,
                    horizontalPosition: 'right',
                    verticalPosition: 'top',
                    panelClass: 'custom-snackbar-success',
                  }
                );
                return new EntityDeleted(action.entityName);
              })
            );
          }
          default: {
            console.log('Invalid choice');
            break;
          }
        }
      },
      onError: (action: DeleteEntity, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new DeleteEntityError(error);
      },
    })
  );

  @Effect() entityDeleted$ = this.actions$.pipe(
    ofType(MasterActionTypes.EntityDeleted),
    fetch({
      run: (action: EntityDeleted, state: MasterPartialState) => {
        switch (action.entityName) {
          case 'shipper': {
            return new LoadShipper();
          }
          case 'consignee': {
            return new LoadConsignee();
          }
          case 'notify': {
            return new LoadNotify();
          }
          case 'deliveryAgent': {
            return new LoadDeliveryAgent();
          }
          default: {
            console.log('Invalid choice');
            break;
          }
        }
      },
      onError: (action: EntityDeleted, error) => {
        this._snackBar.open(error.errors[0].message, null, {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: 'custom-snackbar-error',
        });
        return new DeleteEntityError(error);
      },
    })
  );

  constructor(private actions$: Actions, private _snackBar: MatSnackBar) {}
}
